import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, GripVertical } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'
import { cn } from '@/lib/utils/cn'

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

interface SummaryField {
  id: string
  display_label: string
  source_form_id: string
  source_field_key: string
  aggregation: string
  show_last_updated: boolean
  show_warning_indicator: boolean
  display_order: number
  icon: string | null
  fallback_text: string | null
}

interface Form {
  id: string
  title: string
  instance_type: string
  fields?: { key: string; label: string; type: string }[]
}

async function fetchSummaryConfig(projectId: string) {
  const [configRes, formsRes] = await Promise.all([
    supabase
      .from('summary_card_config')
      .select('*')
      .eq('project_id', projectId)
      .order('display_order'),
    supabase
      .from('forms')
      .select('id, title, instance_type, current_version_id')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .eq('is_published', true),
  ])

  if (configRes.error) throw configRes.error

  const forms: Form[] = (formsRes.data ?? []).map((f) => ({
    id: f.id,
    title: parseTitle(f.title),
    instance_type: f.instance_type,
  }))

  const fields = (configRes.data ?? []).map((c) => ({
    id: c.id,
    display_label: parseTitle(c.display_label),
    source_form_id: c.source_form_id,
    source_field_key: c.source_field_key,
    aggregation: c.aggregation,
    show_last_updated: c.show_last_updated,
    show_warning_indicator: c.show_warning_indicator,
    display_order: c.display_order,
    icon: c.icon,
    fallback_text: c.fallback_text ? parseTitle(c.fallback_text) : null,
  })) as SummaryField[]

  return { fields, forms }
}

async function fetchFormFields(formId: string) {
  const { data: form } = await supabase
    .from('forms')
    .select('current_version_id')
    .eq('id', formId)
    .single()
  if (!form?.current_version_id) return []
  const { data: version } = await supabase
    .from('form_versions')
    .select('definition_snapshot')
    .eq('id', form.current_version_id)
    .single()
  if (!version) return []
  const def = version.definition_snapshot as { fields?: { field_key: string; label: { fa: string } | string; field_type: string }[]; steps?: { fields?: { key: string; label: string; type: string }[] }[] }
  if (Array.isArray(def.fields)) {
    return def.fields.map((f) => ({ key: f.field_key, label: typeof f.label === 'string' ? f.label : f.label?.fa || f.field_key, type: f.field_type }))
  }
  return (def.steps ?? []).flatMap((s) => s.fields ?? [])
}

interface FieldEditorModalProps {
  open: boolean
  onClose: () => void
  projectId: string
  forms: Form[]
  existing?: SummaryField | null
  onSave: (data: Omit<SummaryField, 'id' | 'display_order'>) => void
  saving: boolean
}

function FieldEditorModal({ open, onClose, forms, existing, onSave, saving }: FieldEditorModalProps) {
  const [label, setLabel] = useState('')
  const [formId, setFormId] = useState('')
  const [fieldKey, setFieldKey] = useState('')
  const [aggregation, setAggregation] = useState('single')
  const [showLastUpdated, setShowLastUpdated] = useState(true)
  const [showWarning, setShowWarning] = useState(false)
  const [icon, setIcon] = useState('')
  const [formFields, setFormFields] = useState<{ key: string; label: string; type: string }[]>([])

  useEffect(() => {
    if (existing) {
      setLabel(existing.display_label)
      setFormId(existing.source_form_id)
      setFieldKey(existing.source_field_key)
      setAggregation(existing.aggregation)
      setShowLastUpdated(existing.show_last_updated)
      setShowWarning(existing.show_warning_indicator)
      setIcon(existing.icon ?? '')
    } else {
      setLabel(''); setFormId(''); setFieldKey(''); setAggregation('single')
      setShowLastUpdated(true); setShowWarning(false); setIcon('')
    }
  }, [existing, open])

  useEffect(() => {
    if (!formId) { setFormFields([]); return }
    fetchFormFields(formId).then(setFormFields)
  }, [formId])

  const selectedForm = forms.find((f) => f.id === formId)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? 'ویرایش فیلد خلاصه' : 'افزودن فیلد خلاصه'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>برچسب نمایشی</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="مثال: تاریخ تولد" />
          </div>
          <div className="space-y-1.5">
            <Label>فرم منبع</Label>
            <Select value={formId} onValueChange={setFormId}>
              <SelectTrigger><SelectValue placeholder="انتخاب فرم..." /></SelectTrigger>
              <SelectContent>
                {forms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {formId && (
            <div className="space-y-1.5">
              <Label>فیلد منبع</Label>
              <Select value={fieldKey} onValueChange={setFieldKey}>
                <SelectTrigger><SelectValue placeholder="انتخاب فیلد..." /></SelectTrigger>
                <SelectContent>
                  {formFields.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label || f.key} <span className="text-muted-foreground ms-1 text-xs">({f.key})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {selectedForm?.instance_type === 'repeatable' && (
            <div className="space-y-1.5">
              <Label>تجمیع</Label>
              <Select value={aggregation} onValueChange={setAggregation}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest_instance">آخرین نمونه</SelectItem>
                  <SelectItem value="first_instance">اولین نمونه</SelectItem>
                  <SelectItem value="count">تعداد</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="show_updated"
                checked={showLastUpdated}
                onCheckedChange={(v) => setShowLastUpdated(Boolean(v))}
              />
              <label htmlFor="show_updated" className="text-sm cursor-pointer">نمایش تاریخ آخرین به‌روزرسانی</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="show_warning"
                checked={showWarning}
                onCheckedChange={(v) => setShowWarning(Boolean(v))}
              />
              <label htmlFor="show_warning" className="text-sm cursor-pointer">نمایش نشانگر هشدار</label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>آیکون (اختیاری)</Label>
            <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="مثال: 📅" className="w-20" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button
            onClick={() => onSave({
              display_label: label,
              source_form_id: formId,
              source_field_key: fieldKey,
              aggregation,
              show_last_updated: showLastUpdated,
              show_warning_indicator: showWarning,
              icon: icon || null,
              fallback_text: null,
            })}
            disabled={saving || !label || !formId || !fieldKey}
          >
            {saving ? 'در حال ذخیره...' : 'ذخیره'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SummaryCardContent() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingField, setEditingField] = useState<SummaryField | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['summary-card-config', projectId],
    queryFn: () => fetchSummaryConfig(projectId!),
    enabled: !!projectId,
  })

  const saveMutation = useMutation({
    mutationFn: async (payload: Omit<SummaryField, 'id' | 'display_order'>) => {
      const displayOrder = (data?.fields.length ?? 0) + 1
      if (editingField) {
        const { error } = await supabase
          .from('summary_card_config')
          .update({
            display_label: { fa: payload.display_label },
            source_form_id: payload.source_form_id,
            source_field_key: payload.source_field_key,
            aggregation: payload.aggregation,
            show_last_updated: payload.show_last_updated,
            show_warning_indicator: payload.show_warning_indicator,
            icon: payload.icon,
          })
          .eq('id', editingField.id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('summary_card_config')
          .insert({
            project_id: projectId!,
            display_label: { fa: payload.display_label },
            source_form_id: payload.source_form_id,
            source_field_key: payload.source_field_key,
            aggregation: payload.aggregation,
            show_last_updated: payload.show_last_updated,
            show_warning_indicator: payload.show_warning_indicator,
            icon: payload.icon,
            display_order: displayOrder,
          })
        if (error) throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['summary-card-config', projectId] })
      setEditorOpen(false)
      setEditingField(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      supabase.from('summary_card_config').delete().eq('id', id)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['summary-card-config', projectId] }),
  })

  const fields = data?.fields ?? []
  const forms = data?.forms ?? []

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          فیلدهایی که در بالای صفحه جزئیات هر سابقه نمایش داده می‌شوند را تعریف کنید.
        </p>
        <Button size="sm" onClick={() => { setEditingField(null); setEditorOpen(true) }} className="gap-1.5 shrink-0">
          <Plus className="w-4 h-4" />
          افزودن فیلد
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && fields.length === 0 && (
        <EmptyState
          icon={Plus}
          title="فیلد خلاصه‌ای تعریف نشده"
          description="فیلدهایی که باید در کارت خلاصه سابقه نمایش داده شوند را اضافه کنید"
          action={
            <Button size="sm" onClick={() => setEditorOpen(true)}>
              <Plus className="w-4 h-4 me-1.5" />
              افزودن اولین فیلد
            </Button>
          }
        />
      )}

      {fields.length > 0 && (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {fields.map((field) => {
            const sourceForm = forms.find((f) => f.id === field.source_form_id)
            return (
              <div key={field.id} className="flex items-center gap-3 px-4 py-3">
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-base shrink-0">{field.icon ?? '📋'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{field.display_label}</p>
                  <p className="text-xs text-muted-foreground">
                    {sourceForm?.title ?? '—'} · <span className="font-mono">{field.source_field_key}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm" variant="ghost" className="h-7 w-7 p-0"
                    onClick={() => { setEditingField(field); setEditorOpen(true) }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate(field.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <FieldEditorModal
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingField(null) }}
        projectId={projectId!}
        forms={forms}
        existing={editingField}
        onSave={(d) => saveMutation.mutate(d)}
        saving={saveMutation.isPending}
      />
    </>
  )
}

export function SummaryCardPage() {
  return (
    <div className="flex flex-col h-full">
      <PageHeader title="کارت خلاصه" breadcrumbs={[{ label: 'کارت خلاصه' }]} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          <SummaryCardContent />
        </div>
      </div>
    </div>
  )
}
